import React, { useContext, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { truncate, isEmpty, groupBy } from 'lodash'
import moment from 'moment'

import { Link } from 'react-router-dom'
import { formatTime } from '@/utils/helpers'
import WidgetContext from '@/components/widget/widget.context'
import { Pill } from '@/components/pills/Pill'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'

import './QAActivity.css'

export const QAActivityTable = () => {
  const { handleChangeCsvData } = useContext(WidgetContext)
  const { data, loading } = useSelector((state) => state.scorecards)
  const { organizationid } = useSelector((state) => state.currentUser)
  const [activityEvents, setActivityEvents] = useState([])

  const compareTime = (timeStart, timeEnd) => {
    const startTime = moment(timeStart)
    const stopTime = moment(timeEnd)
    return Math.round(stopTime.diff(startTime, 'minutes', true)).toString()
  }

  const parseScore = (score) => {
    const val = score === 1 ? 'Pass' : score === 0 ? 'Fail' : 'N/A'

    return {
      as: (
        <Pill small success={score === 1} critical={score === 0}>
          {val}
        </Pill>
      ),
      value: val,
    }
  }

  const trimText = (text) => {
    return truncate(text, { length: 50, omission: '...' })
  }

  const columns = [
    { accessor: 'call_id', label: 'Call ID', isSearchable: true },
    { accessor: 'event_count', label: 'Event' },
    { accessor: 'scorecard_user', label: 'Agent', isSearchable: true },
    { accessor: 'call_start_time', label: 'Call Time' },
    { accessor: 'duration', label: 'Call Duration' },
    { accessor: 'scorecard_name', label: 'Scorecard' },
  ]

  const childColumns = [
    { accessor: 'previous_criteria_score', label: 'Previous Score' },
    { accessor: 'new_criteria_score', label: 'New Score' },
    { accessor: 'scorecard_note_text', label: 'Scorecard Note' },
    { accessor: 'scorecard_note_author', label: 'Scorecard Note Author' },
    { accessor: 'criteria_name', label: 'Criteria' },
    { accessor: 'criteria_note_text', label: 'Criteria Note' },
    { accessor: 'criteria_note_author', label: 'Criteria Note Author' },
    { accessor: 'editor_user', label: 'Editor User', isSearchable: true },
    { accessor: 'created_at', label: 'Edited At' },
    { accessor: 'ai_explanation', label: 'AI Explanation' },
  ]

  if (organizationid === 1) {
    // We shouldn't expose this to other users
    const targetColumnIndex = childColumns.findIndex(
      (childColumns) => childColumns.accessor === 'ai_explanation'
    )
    childColumns.splice(targetColumnIndex, 0, {
      accessor: 'ai_response',
      label: 'AI Response',
      isSearchable: true,
      collapsing: true,
    })
  }

  useEffect(() => {
    if (!isEmpty(data?.criteriaAuditEvents)) {
      const updatedActivityEvents = data?.criteriaAuditEvents.map((audit_event) => {
        let ai_response

        if (organizationid === 1) {
          ai_response = {
            as: (
              <div>
                <InteractiveTooltip interactive content={audit_event.ai_response} theme="light">
                  <div>{trimText(audit_event.ai_response)}</div>
                </InteractiveTooltip>
              </div>
            ),
            value: audit_event.ai_response,
          }
        }

        return {
          ...audit_event,
          call_id: {
            as: (
              <Link to={`/calls/${audit_event.call_id}`} className="table-link">
                {audit_event.call_id}
              </Link>
            ),
            value: audit_event.call_id,
          },
          scorecard_user: `${audit_event.first_name} ${audit_event.last_name}`,
          editor_user: `${audit_event.editor_first_name} ${audit_event.editor_last_name}`,
          duration: {
            as: (
              <div>
                {compareTime(audit_event.call_start_time, audit_event.call_end_time)} Minutes
              </div>
            ),
            value: `${compareTime(audit_event.call_start_time, audit_event.call_end_time)} Minutes`,
          },
          call_start_time: formatTime(audit_event.call_start_time),
          created_at: formatTime(audit_event.created_at),
          previous_criteria_score: parseScore(audit_event.previous_criteria_score),
          new_criteria_score: parseScore(audit_event.new_criteria_score),
          scorecard_note_text: {
            as: (
              <div>
                <InteractiveTooltip
                  interactive
                  content={audit_event.scorecard_note_text}
                  theme="light"
                >
                  <div>{trimText(audit_event.scorecard_note_text)}</div>
                </InteractiveTooltip>
              </div>
            ),
            value: audit_event.scorecard_note_text,
          },
          scorecard_note_author: `${audit_event?.scorecard_note_author_first_name} ${audit_event?.scorecard_note_author_last_name}`,
          criteria_note_text: {
            as: (
              <div>
                <InteractiveTooltip
                  interactive
                  content={audit_event?.criteria_note_text || ''}
                  theme="light"
                >
                  <div>{trimText(audit_event?.criteria_note_text)}</div>
                </InteractiveTooltip>
              </div>
            ),
            value: audit_event.criteria_note_text || '',
          },
          criteria_note_author:
            `${audit_event?.criteria_note_author_first_name} ${audit_event?.criteria_note_author_last_name}` ||
            '',
          ai_explanation: {
            as: (
              <div>
                <InteractiveTooltip interactive content={audit_event.ai_explanation} theme="light">
                  <div>{trimText(audit_event.ai_explanation)}</div>
                </InteractiveTooltip>
              </div>
            ),
            value: audit_event.ai_explanation,
          },
          ai_response,
          prompt_has_changed: {
            as: <div>{audit_event.prompt_has_changed ? 'Yes' : 'No'}</div>,
          },
        }
      })
      const grouped = groupBy(
        updatedActivityEvents,
        (e) => `${e.call_id?.value}-${e.scorecard_name}`
      )
      const collapsedRows = Object.entries(grouped).map(([key, row]) => ({
        ...row[0],
        id: key,
        childColumns,
        childTableData: row,
        event_count: row.length,
      }))

      const csvData = {
        headers: [...columns.filter((c) => c.accessor !== 'event_count'), ...childColumns],
        data: updatedActivityEvents.map((row) => ({
          ...row,
          call_id: row.call_id?.value,
          previous_criteria_score: row.previous_criteria_score?.value,
          new_criteria_score: row.new_criteria_score?.value,
          scorecard_note_text: row.scorecard_note_text?.value,
          scorecard_note_author: row.scorecard_note_author,
          criteria_note_text: row.criteria_note_text?.value,
          criteria_note_author: row.criteria_note_author,
          ai_response: organizationid === 1 && row.ai_response?.value,
          ai_explanation: row.ai_explanation?.value,
          duration: row.duration?.value,
        })),
      }
      handleChangeCsvData(csvData)
      setActivityEvents(collapsedRows)
    } else {
      handleChangeCsvData({})
      setActivityEvents([])
    }
  }, [data?.criteriaAuditEvents])

  return (
    <div data-testid="scorecard-exports__table" className="scorecard-audit__table">
      <AdvancedTable
        rows={activityEvents}
        columns={columns}
        loading={loading?.criteriaAuditEvents}
        loadingText="Your data for this report is running. This could take a few minutes..."
        defaultOrder="asc"
        wrapColumnContent={false}
        pagination
        expandable
      />
    </div>
  )
}
